import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';

const DEFAULT_BLOCK_REVEAL_THRESHOLD = 0.08;

// A block with at least this many pixels inside the viewport always counts as
// revealed, even when that is a small fraction of a very tall block (e.g. a map
// whose SVG is 500-600px). Without this cap the ratio-only threshold can leave
// tall blocks hidden because `visibleHeight / targetHeight` never reaches 0.08.
const MAX_REVEAL_TRIGGER_PX = 96;

type RectLike = Pick<DOMRectReadOnly, 'top' | 'bottom' | 'height'>;

function getVisibleHeight(
  targetRect: RectLike,
  viewportRect: RectLike
): number {
  return Math.max(
    0,
    Math.min(targetRect.bottom, viewportRect.bottom) -
      Math.max(targetRect.top, viewportRect.top)
  );
}

function isSufficientlyVisible(
  targetRect: RectLike,
  viewportRect: RectLike,
  threshold: number
): boolean {
  const visibleHeight = getVisibleHeight(targetRect, viewportRect);

  if (visibleHeight <= 0) {
    return false;
  }

  // Height not measurable yet (block hasn't sized) — any overlap counts so the
  // block can't get stranded waiting for a height that arrives asynchronously.
  if (targetRect.height <= 0) {
    return true;
  }

  const requiredVisible = Math.min(
    targetRect.height * threshold,
    MAX_REVEAL_TRIGGER_PX
  );
  return visibleHeight >= requiredVisible;
}

export function useBlockReveal(
  targetRef: RefObject<HTMLElement | null>,
  viewportElement: HTMLElement | null,
  threshold: number = DEFAULT_BLOCK_REVEAL_THRESHOLD,
  pacedEntry: boolean = false
): {
  isRevealed: boolean;
  disableTransition: boolean;
} {
  const [isRevealed, setIsRevealed] = useState(false);
  const [disableTransition, setDisableTransition] = useState(false);

  useLayoutEffect(() => {
    if (isRevealed) {
      return undefined;
    }

    const target = targetRef.current;
    const viewport = viewportElement;

    if (!target || !viewport) {
      return undefined;
    }

    let settled = false;
    let frameId = 0;
    let resizeObserver: ResizeObserver | null = null;
    let intersectionObserver: IntersectionObserver | null = null;

    const teardown = () => {
      if (frameId && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(frameId);
        frameId = 0;
      }
      resizeObserver?.disconnect();
      resizeObserver = null;
      intersectionObserver?.disconnect();
      intersectionObserver = null;
    };

    const reveal = (instant: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      if (instant) {
        setDisableTransition(true);
      }
      setIsRevealed(true);
      teardown();
    };

    const isVisibleNow = () =>
      getVisibleHeight(
        target.getBoundingClientRect(),
        viewport.getBoundingClientRect()
      ) > 0;

    if (pacedEntry) {
      frameId = requestAnimationFrame(() => {
        frameId = requestAnimationFrame(() => reveal(false));
      });
      return teardown;
    }

    // 1. No-flash fast path: the block already overlaps the viewport at first
    //    measure, so reveal instantly without animating.
    if (isVisibleNow()) {
      reveal(true);
      return undefined;
    }

    // No IntersectionObserver (non-DOM/test env): fail open rather than leaving
    // content permanently invisible.
    if (typeof IntersectionObserver !== 'function') {
      reveal(true);
      return undefined;
    }

    setDisableTransition(false);

    // 2. Scroll-driven reveal once the block crosses the threshold.
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry || !entry.isIntersecting) {
          return;
        }

        const viewportRect = entry.rootBounds ?? viewport.getBoundingClientRect();
        if (!isSufficientlyVisible(entry.boundingClientRect, viewportRect, threshold)) {
          return;
        }

        intersectionObserver?.unobserve(entry.target);
        reveal(false);
      },
      {
        root: viewport,
        rootMargin: '0px',
        threshold: [0, threshold, 1],
      }
    );

    intersectionObserver.observe(target);

    // 3. Robustness against a degenerate first measurement. The synchronous
    //    check above can read zero overlap when layout hasn't settled at this
    //    commit, or when the block's own height resolves later (a map sizes its
    //    SVG after paint, an image loads lazily). In those cases the block is
    //    actually in view but the IntersectionObserver's initial callback
    //    reports below-threshold, so it would stay hidden until the user
    //    scrolls. Re-measure after the next paint and whenever the block or
    //    viewport resizes, so an in-view block reveals on its own.
    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(() => {
        if (isVisibleNow()) {
          reveal(false);
        }
      });
      resizeObserver.observe(target);
      resizeObserver.observe(viewport);
    }

    if (typeof requestAnimationFrame === 'function') {
      frameId = requestAnimationFrame(() => {
        frameId = 0;
        if (isVisibleNow()) {
          reveal(false);
        }
      });
    }

    return teardown;
  }, [isRevealed, targetRef, threshold, viewportElement, pacedEntry]);

  useEffect(() => {
    if (!disableTransition || !isRevealed || typeof window === 'undefined') {
      return undefined;
    }

    if (
      typeof window.requestAnimationFrame === 'function' &&
      typeof window.cancelAnimationFrame === 'function'
    ) {
      const frameId = window.requestAnimationFrame(() => {
        setDisableTransition(false);
      });

      return () => {
        window.cancelAnimationFrame(frameId);
      };
    }

    const timeoutId = window.setTimeout(() => {
      setDisableTransition(false);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [disableTransition, isRevealed]);

  return { isRevealed, disableTransition };
}
